import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Input, Radio, Select, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import DocumentUpload from '../DocumentUpload';
import { saveOrUpdateDocument } from 'api/documentApi';
import { gpSaveOrUpdateDocument } from 'api/group';
import { DocumentUploadData } from 'types/document';
import s from './s.module.less';
interface UploadDocumentModalProps {
    open: boolean;
    clientId: number;
    onClose: () => void;
    onSuccess: () => void;
    source?: string;
    isGroupPractice?: boolean;
}

// Tag 选项
export const TAG_OPTIONS = [
    'Identity & Eligibility',
    'Consents & Legal',
    'Clinical Notes',
    'Labs & Tests',
    'Administrative',
    'Others',
];

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ open, clientId, onClose, onSuccess, source, isGroupPractice }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (open) {
            form.resetFields();
            // 初始化一个空的文档
            form.setFieldsValue({
                documents: [{}],
            });
        }
    }, [open, form]);

    const handleOk = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const documents: DocumentUploadData[] = values.documents || [];
            const saveApi = isGroupPractice ? gpSaveOrUpdateDocument : saveOrUpdateDocument;
            const res = await saveApi(documents.map(item => ({
                name: item.name,
                tag: item.tag,
                fileUploadId: item.fileUploadId,
                visibleSubjectIds: item.visibleToPatient ? [clientId] : [],
                note: item.noteToPatient,
                source,
            })), clientId.toString());
            if (!res.error) {
                message.success('Document uploaded successfully.');
                onSuccess();
                onClose();
            }

            form.resetFields();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={[
                <Button onClick={handleClose} className={s.cancelButton}>Cancel</Button>,
                <Button type="primary" onClick={handleOk} className={s.uploadButton} loading={loading}>Upload</Button>,
            ]}
            width={640}
            className={s.modal}
            closeIcon={<CloseOutlined className={s.closeIcon} />}
            title="Upload document"
            bodyStyle={{
                maxHeight: 'calc(90vh - 110px)',
                overflowY: 'auto',
                padding: '24px',
            }}
        >
            <div className={s.modalContent}>
                <Form form={form} layout="vertical" className={s.form} initialValues={{ documents: [{}] }}>
                    <Form.List name="documents">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <div key={field.key} className={s.documentSection}>
                                        <div className={s.sectionHeader}>
                                            <h3 className={s.sectionTitle}>Document {index + 1}</h3>
                                            {fields.length > 1 && (
                                                <Button
                                                    onClick={() => remove(field.name)}
                                                    className={s.removeButton}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>

                                        <div className={s.formItemGroup}>
                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'name']}
                                                label="Name"
                                                rules={[{ required: true, message: 'Please input the document name' }]}
                                                className={s.formItem}
                                            >
                                                <Input placeholder="Enter file name" />
                                            </Form.Item>

                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'tag']}
                                                label="Tag"
                                                className={s.formItem}
                                            >
                                                <Select
                                                    placeholder="Select or enter tags"
                                                    className={s.tagSelect}
                                                    mode="tags"
                                                    allowClear
                                                    maxTagCount={3}
                                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                                                    tokenSeparators={[',']}
                                                >
                                                    {TAG_OPTIONS.map((tag) => (
                                                        <Select.Option key={tag} value={tag}>
                                                            {tag}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>

                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'fileUploadId']}
                                                label="File"
                                                rules={[{ required: true, message: 'Please upload a file' }]}
                                                className={s.formItem}
                                            >
                                                <DocumentUpload
                                                    clientId={clientId}
                                                    accept='image/*,application/pdf'
                                                    isGroupPractice={isGroupPractice}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                {...field}
                                                name={[field.name, 'visibleToPatient']}
                                                label="Visible to patient?"
                                                className={s.formItem}
                                                initialValue={true}
                                            >
                                                <Radio.Group className={s.radioGroup}>
                                                    <Radio value={true}>Yes</Radio>
                                                    <Radio value={false}>No</Radio>
                                                </Radio.Group>
                                            </Form.Item>

                                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                                const prevVisible = prevValues?.documents?.[field.name]?.visibleToPatient;
                                                const currentVisible = currentValues?.documents?.[field.name]?.visibleToPatient;
                                                return prevVisible !== currentVisible;
                                            }}>
                                                {({ getFieldValue }) => {
                                                    const visibleToPatient = getFieldValue(['documents', field.name, 'visibleToPatient']);
                                                    if (visibleToPatient === false) {
                                                        return null;
                                                    }
                                                    return (
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'noteToPatient']}
                                                            label="Note to patient"
                                                            className={s.formItem}
                                                        >
                                                            <div>
                                                                <div className={s.noteDescription}>
                                                                    Optional message to let the patient know what this document is for
                                                                </div>
                                                                <Input placeholder="Enter note to patient" />
                                                            </div>
                                                        </Form.Item>
                                                    );
                                                }}
                                            </Form.Item>
                                        </div>
                                    </div>
                                ))}

                                <div className={s.addDocumentLink}>
                                    <Button type="link" onClick={() => add()} className={s.addLink}>
                                        Upload another document
                                    </Button>
                                </div>
                            </>
                        )}
                    </Form.List>
                </Form>
            </div>
        </Modal >
    );
};

export default UploadDocumentModal;


